// packages/report-components/src/Text.tsx
//
// Body-text primitive. Mirrors Mantine's Text: size / weight / colour
// variants with a semantic-intent API rather than raw pixel values.
//
// Rendered element:
//   `inline` → <span>
//   block    → <p>
// Authors can pass <em>/<strong>/<code>/<a> as children — the stylesheet
// in components.css gives those children the right look.

import * as React from "react";

export type TextSize = "xs" | "sm" | "md" | "lg" | "xl";
export type TextWeight = "regular" | "medium" | "bold";
export type TextColor =
  | "default"
  | "muted"
  | "subtle"
  | "danger"
  | "success";

export interface TextProps {
  /** Semantic size. Defaults to "md". */
  size?: TextSize;
  /** Font weight. Defaults to "regular". */
  weight?: TextWeight;
  /** Colour role. Defaults to "default". */
  color?: TextColor;
  /** If true, render an inline <span> instead of a block <p>. Use inline
   * when Text is composed inside a sentence. Defaults to false. */
  inline?: boolean;
  /** Align content. Defaults to left. */
  align?: "left" | "center" | "right";
  children?: React.ReactNode;
}

const SIZE_TOKEN: Record<TextSize, string> = {
  xs: "var(--verity-font-size-xs, 12px)",
  sm: "var(--verity-font-size-sm, 13px)",
  md: "var(--verity-font-size-md, 15px)",
  lg: "var(--verity-font-size-lg, 17px)",
  xl: "var(--verity-font-size-xl, 22px)",
};

const WEIGHT_VALUE: Record<TextWeight, number> = {
  regular: 400,
  medium: 500,
  bold: 700,
};

const COLOR_TOKEN: Record<TextColor, string> = {
  default: "var(--verity-color-text, #111827)",
  muted: "var(--verity-color-text-muted, #6b7280)",
  subtle: "var(--verity-color-text-subtle, #9ca3af)",
  danger: "var(--verity-color-danger, #dc2626)",
  success: "var(--verity-color-success, #059669)",
};

export function Text({
  size = "md",
  weight = "regular",
  color = "default",
  inline = false,
  align = "left",
  children,
}: TextProps): React.ReactElement {
  const style: React.CSSProperties = {
    fontSize: SIZE_TOKEN[size],
    fontWeight: WEIGHT_VALUE[weight],
    color: COLOR_TOKEN[color],
    lineHeight: "var(--verity-line-height-normal, 1.5)",
    textAlign: align,
    // Avoid the parent's flex stretching causing long words to overflow.
    wordBreak: "break-word",
  };

  const props = {
    className: "verity-text",
    "data-size": size,
    "data-weight": weight,
    "data-color": color,
    style,
  };

  return inline
    ? React.createElement("span", props, children)
    : React.createElement("p", props, children);
}
